/**
 * Screen data for B1 · হোম.
 *
 * Every Bangla string is copied from `Oprappo P0 Screens.dc.html`, frame "B1 · হোম".
 * Nothing is invented — docs/01 § prohibited: "Every Bangla string in the design is
 * final. Missing strings: ask."
 *
 * WHAT IS LEFT IN THIS FILE IS CHROME. The eight quick-action labels, the greeting, the
 * search placeholder and the two section headings are the same for every person who opens
 * the app, so they are drawn constants. Everything that differs per person — the name, the
 * live order, the repeat cards — is read from the API in `./api.ts`. The screen used to
 * carry all of it here, which is how it came to greet everybody as রফিকুল ইসলাম and show
 * them an order they never placed.
 *
 * Money is integer minor units (paisa) per docs/05 § conventions, so `৳ 1,340` is
 * 134000 and the screen formats it.
 */
import type { IconName } from '@oprappo/ui';
import type { GlyphName } from '../onboarding/glyphs.js';

/**
 * A quick action's mark is either a § 01 icon or one of the P0-only paths. The union is
 * explicit rather than a string, so a typo cannot render an empty cell.
 */
export type Mark =
  | { from: 'icon'; name: IconName }
  | { from: 'glyph'; name: GlyphName };

export interface QuickAction {
  id: string;
  label_bn: string;
  mark: Mark;
  /** The last cell, `সব দেখুন`, is drawn `600` in teal rather than `500` in ink2. */
  emphasis?: boolean;
  to?: string;
}

/**
 * Eight actions, in the drawn order. The frame note records the test they passed:
 * "All eight labels hold on one line at 360."
 */
export const QUICK_ACTIONS: readonly QuickAction[] = [
  { id: 'recharge', label_bn: 'রিচার্জ', mark: { from: 'icon', name: 'recharge' } },
  { id: 'bill', label_bn: 'বিল পে', mark: { from: 'icon', name: 'bill' } },
  { id: 'send-money', label_bn: 'টাকা পাঠান', mark: { from: 'glyph', name: 'sendArrow' } },
  { id: 'food', label_bn: 'খাবার', mark: { from: 'glyph', name: 'food' } },
  { id: 'ride', label_bn: 'রাইড', mark: { from: 'icon', name: 'transport' } },
  { id: 'market', label_bn: 'বাজার', mark: { from: 'icon', name: 'market' } },
  { id: 'service', label_bn: 'সার্ভিস', mark: { from: 'glyph', name: 'service' } },
  {
    id: 'all',
    label_bn: 'সব দেখুন',
    mark: { from: 'icon', name: 'explore' },
    emphasis: true,
    to: '/explore',
  },
];

/**
 * The FIXED labels in the teal header — the two that are chrome rather than content.
 *
 * `name_bn` used to live here, holding `রফিকুল ইসলাম`, and it greeted every person who
 * ever opened the app by the name of the person the frame was drawn for. It is gone: the
 * name is the signed-in person's own, read from `GET /v1/account/profile`
 * (`./api.ts § fetchName`), and a visitor with no account gets the greeting alone rather
 * than somebody else's name.
 *
 * The greeting and the placeholder stay, because they are the same sentence for
 * everybody. Both are transcribed from `Oprappo P0 Screens.dc.html`, frame B1.
 */
export const HOME_HEADER = {
  greeting_bn: 'আসসালামু আলাইকুম',
  placeholder_bn: 'কী প্রয়োজন?',
} as const;

/**
 * The two section headings, and the long-press hint beside the second.
 *
 * Headings, not content. `চলমান` labels the live-order block whatever is in it, and the
 * block's title, detail line and percent are the server's — `GET /v1/activity/:id`'s
 * `status` card, which is `ActivityStatusCard` in `@oprappo/contracts`.
 *
 * WHAT WAS HERE BEFORE, AND WHY IT HAD TO GO: a `LIVE_ORDER` constant holding
 * `টেকনিশিয়ান পথে আছেন`, `রহিম ইলেকট্রিক · আনুমানিক ২০ মিনিট` and `percent: 60`, and a
 * `REPEAT_CARDS` array holding two payments. Every person who opened the app was shown a
 * technician on the way to somebody else's flat and two bills they had never paid. The
 * screen looked finished and was telling every user the same lie.
 */
export const LIVE_ORDER = {
  heading_bn: 'চলমান',
} as const;

export const REPEAT = {
  heading_bn: 'আবার প্রয়োজন হতে পারে',
  hint_bn: 'লম্বা চাপে লুকান',
} as const;
